import { type AllergyGroup, allergyGroupRecords } from '@liza/fhir-utils';
import { cn } from '@liza/ui/utils';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { AllergyRecordItem, type AllergyRecordItemProps } from './AllergyRecordItem';
import { joinDescriptionParts, reactionsLine } from './allergyRowDescription';

export type AllergyGroupRowProps = Omit<AllergyRecordItemProps, 'record' | 'lead'> & {
  group: AllergyGroup;
};

/** The severity tier as a short label, coloured when it carries a warning. Unrecorded is silent. */
const tierLabel = (tier: AllergyGroup['tier'], t: TFunction<'records'>): React.ReactNode => {
  if (tier === 'high') return <span className="font-medium text-destructive">{t('allergyRiskHigh')}</span>;
  if (tier === 'low') return t('allergyRiskLow');
  return undefined;
};

/**
 * One allergen. A group with a single source record renders as a plain row. A group several rows
 * collapsed into renders as a header carrying the merged reactions, tier and source count, which
 * expands to the individual records; it opens on first render when it holds the selected record.
 */
export const AllergyGroupRow = ({ group, ...itemProps }: AllergyGroupRowProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { selectedAllergyId } = itemProps;
  const containsSelected =
    selectedAllergyId !== undefined && allergyGroupRecords(group).some((record) => record.id === selectedAllergyId);
  const [open, setOpen] = useState(containsSelected);

  // A later selection (the sidebar navigating to another record while the list stays mounted) must
  // open its group too; a manual collapse otherwise stays as the user left it.
  useEffect(() => {
    if (containsSelected) setOpen(true);
  }, [containsSelected, selectedAllergyId]);

  const tier = tierLabel(group.tier, t);

  if (group.merged.length === 0) {
    return <AllergyRecordItem record={group.record} lead={tier} {...itemProps} />;
  }

  const count = group.merged.length + 1;
  const description = joinDescriptionParts([
    tier,
    reactionsLine(group.reactions, t),
    group.sources > 1 ? t('allergySources', { count: group.sources }) : undefined,
  ]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={`allergy-group-${group.key}`}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-auto w-full items-center justify-between gap-2 px-3 py-2 text-left font-normal"
          aria-label={t('showAllergyRecords', { name: group.name })}
          data-testid="allergy-group-trigger"
        >
          <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <span
                className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-xs font-medium text-muted-foreground tabular-nums"
                data-testid="record-list-item-count"
              >
                {count}×
              </span>
              <span className="truncate">{group.name || t('unknownAllergy')}</span>
            </span>
            <span className="text-xs text-muted-foreground">{description}</span>
          </span>
          <ChevronDownIcon
            className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 space-y-1 border-l pl-2">
          {allergyGroupRecords(group).map((record) => (
            <AllergyRecordItem key={record.id} record={record} {...itemProps} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
